import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '@/store/store'
import {
  setThemeMode,
  setColorScheme,
  setSystemPreference,
  setReducedMotion,
  setFontSize,
  toggleTheme,
  ThemeMode,
  ColorScheme,
} from '@/store/slices/themeSlice'

export const useTheme = () => {
  const dispatch = useDispatch()
  const theme = useSelector((state: RootState) => state.theme)

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement
    
    // Remove all theme classes
    root.classList.remove('light', 'dark')
    
    // Determine actual theme
    let actualTheme: 'light' | 'dark'
    if (theme.mode === 'system') {
      actualTheme = theme.systemPreference
    } else {
      actualTheme = theme.mode
    }
    
    // Apply theme class
    root.classList.add(actualTheme)
    
    // Apply color scheme
    root.setAttribute('data-color-scheme', theme.colorScheme)
    
    // Apply font size
    root.setAttribute('data-font-size', theme.fontSize)
    
    // Apply reduced motion
    if (theme.reducedMotion) {
      root.style.setProperty('--motion-duration', '0s')
    } else {
      root.style.removeProperty('--motion-duration')
    }
  }, [theme])

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = (e: MediaQueryListEvent) => {
      dispatch(setSystemPreference(e.matches ? 'dark' : 'light'))
    }
    
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [dispatch])

  // Listen for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    
    const handleChange = (e: MediaQueryListEvent) => {
      dispatch(setReducedMotion(e.matches))
    }
    
    // Set initial value
    dispatch(setReducedMotion(mediaQuery.matches))
    
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [dispatch])

  // Get computed theme (resolves 'system' to actual theme)
  const computedTheme = theme.mode === 'system' ? theme.systemPreference : theme.mode

  return {
    // Current theme state
    mode: theme.mode,
    colorScheme: theme.colorScheme,
    fontSize: theme.fontSize,
    reducedMotion: theme.reducedMotion,
    systemPreference: theme.systemPreference,
    computedTheme,
    
    // Actions
    setMode: (mode: ThemeMode) => dispatch(setThemeMode(mode)),
    setColorScheme: (scheme: ColorScheme) => dispatch(setColorScheme(scheme)),
    setFontSize: (size: 'sm' | 'base' | 'lg' | 'xl') => dispatch(setFontSize(size)),
    toggle: () => dispatch(toggleTheme()),
    
    // Utilities
    isDark: computedTheme === 'dark',
    isLight: computedTheme === 'light',
    isSystemMode: theme.mode === 'system',
  }
} 